import { useEffect, useState } from 'react';
import { useFiltersStore } from '@/store/useFiltersStore';
import { getSurveyResponses } from '@/services/survey.service';
import { getAnalyticsMetrics } from '@/services/analytics.service';
import type { SurveyResponse, AnalyticsMetrics } from '@/types';
import KPICards from '@/components/Dashboard/KPICards';
import FiltersPanel from '@/components/Dashboard/FiltersPanel';
import ResponsesTable from '@/components/Dashboard/ResponsesTable';
import AnalyticsCharts from '@/components/Dashboard/AnalyticsCharts';

const AdminDashboardPage = () => {
  const { filters } = useFiltersStore();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [_selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [responsesData, metricsData] = await Promise.all([
          getSurveyResponses(filters),
          getAnalyticsMetrics(filters),
        ]);
        setResponses(responsesData);
        setMetrics(metricsData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-primary-600 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-lg font-semibold text-gray-700">Loading dashboard...</p>
          <p className="mt-2 text-sm text-gray-500">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {metrics && <KPICards metrics={metrics} />}
      <FiltersPanel />
      {metrics && <AnalyticsCharts metrics={metrics} responses={responses} />}
      <ResponsesTable
        responses={responses}
        loading={loading}
        onSelectResponse={setSelectedResponse}
      />
    </div>
  );
};

export default AdminDashboardPage;

